/**
 * Light/dark theme helpers.
 *
 * The visual switch is done entirely by remapping the design-token CSS
 * variables under `html.dark` in styles.css, so every `bg-cream` / `text-ink`
 * / etc. utility flips automatically. This module only manages WHICH theme is
 * active: it toggles the `dark` class on <html>, mirrors it to color-scheme +
 * the theme-color meta, and persists the choice.
 *
 * Default is LIGHT (dark is opt-in): the marketing site is an intentionally
 * light, brand-tuned surface, so a visitor's OS dark preference must not
 * silently repaint it. The anti-FOUC inline script in index.html applies the
 * stored choice before first paint; this module keeps the toggle in sync.
 */

export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "letsloop-theme";

// Keep in sync with the html.dark / :root values in styles.css.
//
// I removed this on 2026-07-31 believing theme-color was what made the iOS
// toolbars render solid. That was WRONG, and the live site disproves it: it
// carries theme-color, carries no viewport-fit, and is transparent. Restored,
// because removing it cost Android and PWA chrome tinting and bought nothing.

// Light value is sampled from the rendered mesh at the top of the hero, not
// the page cream: this colour paints the iOS status-bar strip, and cream there
// is the white band above the gradient. It does not affect the bottom toolbar's
// translucency (a docked full-width element did that, see JoinBar).
const THEME_COLOR = { light: "#f8d2b0", dark: "#12151b" } as const;

/** Apply a theme to the document (class + color-scheme + theme-color meta). */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.classList.toggle("dark", theme === "dark");
  el.style.colorScheme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[theme]);
}

/** The theme currently applied to the document (matches the anti-FOUC script). */
export function getActiveTheme(): Theme {
  if (typeof document === "undefined") return "light";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/** Flip the active theme, persist it, and return the new value. */
export function toggleTheme(): Theme {
  const next: Theme = getActiveTheme() === "dark" ? "light" : "dark";
  applyTheme(next);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Private-mode / storage-disabled: the toggle still works for the session.
  }
  return next;
}
